const FRAME_COUNT = 10;
const THUMB_HEIGHT = 56;

type AnyCanvas = HTMLCanvasElement | OffscreenCanvas;

function createCanvas(width: number, height: number): AnyCanvas {
  if (typeof OffscreenCanvas !== 'undefined') return new OffscreenCanvas(width, height);
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  return canvas;
}

function canvasToBlob(canvas: AnyCanvas): Promise<Blob> {
  if ('convertToBlob' in canvas) {
    return canvas.convertToBlob({ type: 'image/jpeg', quality: 0.72 });
  }
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('Thumbnail encode failed'))),
      'image/jpeg',
      0.72,
    );
  });
}

function seekTo(video: HTMLVideoElement, time: number, timeoutMs = 4000): Promise<void> {
  return new Promise((resolve, reject) => {
    const timer = window.setTimeout(() => {
      video.onseeked = null;
      reject(new Error('Thumbnail seek timed out'));
    }, timeoutMs);
    video.onseeked = () => {
      window.clearTimeout(timer);
      video.onseeked = null;
      resolve();
    };
    video.currentTime = time;
  });
}

/**
 * Capture N evenly spaced thumbnails from a detached video element.
 * Frames are drawn into an OffscreenCanvas where supported (encode work off
 * the layout path) and returned as object URLs — callers own revocation.
 * Best-effort: any failure returns whatever frames were captured.
 */
export async function buildFilmstrip(
  sourceUrl: string,
  duration: number,
  frameCount = FRAME_COUNT,
): Promise<string[]> {
  const urls: string[] = [];
  if (!Number.isFinite(duration) || duration <= 0) return urls;

  const video = document.createElement('video');
  video.muted = true;
  video.playsInline = true;
  video.preload = 'auto';
  video.src = sourceUrl;

  try {
    await new Promise<void>((resolve, reject) => {
      const timer = window.setTimeout(() => reject(new Error('Thumbnail source timed out')), 8000);
      video.onloadeddata = () => {
        window.clearTimeout(timer);
        resolve();
      };
      video.onerror = () => {
        window.clearTimeout(timer);
        reject(new Error('Thumbnail source failed to load'));
      };
    });

    const aspect = video.videoWidth / Math.max(1, video.videoHeight);
    const width = Math.max(24, Math.round(THUMB_HEIGHT * aspect));
    const canvas = createCanvas(width, THUMB_HEIGHT);
    const context = canvas.getContext('2d') as
      CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D | null;
    if (!context) return urls;

    for (let i = 0; i < frameCount; i += 1) {
      const time = ((i + 0.5) / frameCount) * duration;
      await seekTo(video, Math.min(time, Math.max(0, duration - 0.05)));
      context.drawImage(video, 0, 0, width, THUMB_HEIGHT);
      const blob = await canvasToBlob(canvas);
      urls.push(URL.createObjectURL(blob));
    }
  } catch {
    // Partial strips are fine — the timeline degrades to a plain track.
  } finally {
    video.removeAttribute('src');
    video.load();
  }
  return urls;
}
